// Cellery Soapbox — doc viewer (CONTRACTS §6/§7).
// Renders the gated doc in a same-origin iframe and drives annotation directly
// on iframe.contentDocument: inject highlight styles, rehydrate highlights,
// capture selections, and wire the comment Sidebar.
//
// Editors can also edit content inline (full WYSIWYG): edit mode loads the
// shared draft (/draft/**), makes the body contentEditable, autosaves the draft
// (POST /draft), and promotes it to a new immutable version (POST /commit) once
// every comment on the current version is resolved. While editing, the
// annotation/highlight effects are gated OFF so decoration is never injected
// into — or serialized from — the content being edited.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { Sidebar } from "../annotation/Sidebar";
import { SharePanel } from "../components/SharePanel";
import { EditToolbar } from "../wysiwyg/EditToolbar";
import {
  describeAnchorFromSelection,
  rangeFromAnchor,
} from "../annotation/anchor";
import {
  attachHighlightClick,
  captureAssetTags,
  clearHighlights,
  ensureHighlightStyles,
  redrawHighlights,
  removeHighlightStyles,
  serializeCleanHtml,
} from "../annotation/highlighter";
import {
  clearGeneratedContainers,
  markGeneratedContainers,
} from "../wysiwyg/placeholders";
import {
  createAnnotation,
  addComment,
  getDoc,
  listAnnotations,
  listVersions,
  watchDoc,
} from "../firestore";
import {
  commitVersion,
  saveDraft,
  StaleVersionError,
  UnresolvedCommentsError,
} from "../edit";
import {
  joinPresence,
  watchPresence,
  type PresenceHandle,
  type PresentUser,
} from "../presence";
import { setAccess } from "../access";
import { setPersonalTrust, setTrusted, watchPersonalTrust } from "../trust";
import { trackView, type ViewTracker } from "../stats";
import { duplicateDoc } from "../folders";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { SlidePanel } from "../components/SlidePanel";
import { COMMENTS_TOGGLE_ID, ViewerToolbar } from "../components/ViewerToolbar";
import { StatsPanel } from "../components/StatsPanel";
import { ImageExportDialog } from "../components/ImageExportDialog";
import { useToast } from "../components/Toast";
import {
  useCoarsePointer,
  useIsPhone,
  useIsSingleColumn,
} from "../hooks/useMediaQuery";
import { printDoc } from "../export/pdf";
import { downloadHtml, downloadZip } from "../export/html";
import type { Anchor, Annotation, DocMeta, DocVersion } from "../types";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";

export function DocViewer() {
  const { docId, versionId: versionParam } = useParams<{
    docId: string;
    versionId?: string;
  }>();
  const navigate = useNavigate();
  const { user, emailLower } = useAuth();
  const { error: toastError, success } = useToast();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  // Export + presentation
  const [imageExportOpen, setImageExportOpen] = useState(false);
  const [presenting, setPresenting] = useState(false);
  // Slide navigator (section.section decks only).
  const [isSlideDeck, setIsSlideDeck] = useState(false);

  const [meta, setMeta] = useState<DocMeta | null>(null);
  const [versions, setVersions] = useState<DocVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnId, setSelectedAnnId] = useState<string | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<Anchor | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);

  // Comments panel visibility. `null` = follow the automatic rule (open only
  // when the doc actually has threads, so an empty panel never eats a third of
  // the width); true/false = an explicit choice, which sticks until the reader
  // leaves this doc/version. Selecting text always forces it open — see below.
  const [commentsPref, setCommentsPref] = useState<boolean | null>(null);
  const commentsOpen = commentsPref ?? annotations.length > 0;

  // Mobile layout: at ≤860px the viewer is a single column with a Doc/Comments
  // toggle (the iframe + Sidebar stay mounted; only CSS `display` switches, so
  // the doc never reloads). Desktop keeps the two-column grid.
  const isSingleColumn = useIsSingleColumn();
  const isPhone = useIsPhone();
  const coarse = useCoarsePointer();
  const [pane, setPane] = useState<"doc" | "comments">("doc");
  // Touch selection has no hover affordance, so a floating "Add comment" button
  // appears next to the selection (parent-side, positioned in viewport coords).
  const [selAction, setSelAction] = useState<{ top: number; left: number } | null>(
    null
  );

  // ONE comments-visibility intent across both layouts: the desktop grid
  // collapses a track (`commentsPref`), ≤860px swaps panes (`pane`). Everything
  // that shows or hides comments goes through setCommentsVisible, so "hide" means
  // the same thing at every width and the phone overflow item stops being a no-op
  // (it wrote commentsPref, which single-column layout ignores).
  const commentsVisible = isSingleColumn ? pane === "comments" : commentsOpen;
  const commentsVisibleRef = useRef(commentsVisible);
  commentsVisibleRef.current = commentsVisible;
  const isSingleColumnRef = useRef(isSingleColumn);
  isSingleColumnRef.current = isSingleColumn;

  // Stable identity — layout is read through a ref — so the annotation-wiring
  // effect below can call it without re-subscribing on every breakpoint change.
  const setCommentsVisible = useCallback((next: boolean) => {
    if (isSingleColumnRef.current) setPane(next ? "comments" : "doc");
    else setCommentsPref(next);
  }, []);

  // Showing the panel is IMPERATIVE, done at the two places that actually mean
  // "I want to talk about this": a tapped highlight, and the selection FAB. It
  // used to be an effect on selectedAnnId/pendingAnchor, which was wrong three
  // ways — re-clicking the SAME highlight wrote identical state so the effect
  // never re-fired and the panel stayed shut; the effect re-ran on every 860px
  // crossing and undid an explicit close; and any stray text selection overrode
  // the reader's decision to collapse it, which is why it felt unclosable.
  //
  // The × lives inside the panel, so closing unmounts the focused element and
  // focus would fall to <body>. Hand it to the toolbar toggle — in an effect, not
  // here, so the toggle has already re-rendered with the right aria-pressed by
  // the time a screen reader announces it.
  const restoreFocusRef = useRef(false);
  const closeComments = useCallback(() => {
    setCommentsVisible(false);
    // Drop the live selection with it. Otherwise the anchor keeps updating
    // invisibly and reopening the panel greets you with a composer quoting a
    // selection you made minutes ago.
    setPendingAnchor(null);
    restoreFocusRef.current = true;
  }, [setCommentsVisible]);

  const toggleComments = useCallback(() => {
    if (commentsVisibleRef.current) {
      closeComments();
      return;
    }
    // Opening from the toolbar means "show me the comments", not "comment on
    // what I just selected" — drop an anchor the reader never acted on, so the
    // panel never opens onto a composer quoting a selection they abandoned.
    setPendingAnchor(null);
    setCommentsVisible(true);
  }, [closeComments, setCommentsVisible]);

  useEffect(() => {
    if (commentsVisible || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    document.getElementById(COMMENTS_TOGGLE_ID)?.focus();
  }, [commentsVisible]);

  // Set by the annotation-wiring effect; re-run when visibility flips so closing
  // the panel over a live selection surfaces the FAB immediately (and opening it
  // takes the FAB away) instead of waiting for the next scroll or reselect.
  const repositionFabRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    repositionFabRef.current?.();
  }, [commentsVisible]);

  // --- Editing state ----------------------------------------------------
  const [editing, setEditing] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [commitBusy, setCommitBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  // Whether an unpublished draft exists for the current version (so the viewer
  // shows the draft, not the published bytes, and edits persist after exiting
  // edit mode). Synced from meta.draft; flipped optimistically on save/commit.
  const [draftPresent, setDraftPresent] = useState(false);
  // Version the active draft forks from (captured when edit mode is entered).
  const editBaseRef = useRef<string>("");
  // Latest debounced-save flush, exposed so commit/exit can force it first.
  // Resolves false when the draft did NOT reach the server, so callers that
  // publish can refuse rather than promoting stale bytes.
  const pendingFlushRef = useRef<null | (() => Promise<boolean>)>(null);
  // True when the editable DOM has unsaved changes since the last save.
  const dirtyRef = useRef(false);

  // Bumped on each iframe `load` to re-run DOM-bound effects against the new doc.
  const [docEpoch, setDocEpoch] = useState(0);

  // --- Presence + live collaboration ------------------------------------
  const [present, setPresent] = useState<PresentUser[]>([]);
  const presenceRef = useRef<PresenceHandle | null>(null);
  // A remote editor saved a newer draft while WE are editing — soft conflict.
  const [remoteEditor, setRemoteEditor] = useState<string | null>(null);
  // Bumped to force the draft iframe to refetch when a peer's save lands.
  const [draftReloadToken, setDraftReloadToken] = useState(0);

  // Bumped when the owner trusts the doc: it re-keys the iframe so it remounts and
  // re-parses WITHOUT the sandbox, letting the now-trusted bundle's scripts run.
  const [trustReloadToken, setTrustReloadToken] = useState(0);
  const [trustConfirmOpen, setTrustConfirmOpen] = useState(false);
  const [trustBusy, setTrustBusy] = useState(false);
  // Personal trust (§6c): this account alone has lifted the sandbox. Never
  // written to meta.trusted, so it cannot change what anyone else sees.
  const [personallyTrusted, setPersonallyTrusted] = useState(false);
  // Whether the loaded bundle actually contains <script> tags. Sandboxing only
  // MATTERS when it does, so trust affordances stay silent on static uploads
  // rather than nagging about a doc that already renders correctly.
  const [hasScripts, setHasScripts] = useState(false);
  const [trustBannerDismissed, setTrustBannerDismissed] = useState(false);
  // Refs read inside the (stable) live-sync subscription, which must not
  // resubscribe on every edit/version change.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const lastDraftStampRef = useRef<number | null>(null);
  const lastVersionRef = useRef<string | null>(null);
  const firstSnapRef = useRef(true);
  // Iframe scrollTop captured before a live refresh, restored after reload.
  const restoreScrollRef = useRef<number | null>(null);
  // The author's own asset tags (script/style/link/meta/base), captured at load
  // BEFORE any app injection. serializeCleanHtml drops anything not in here, so
  // neither our edit chrome nor a browser extension's injected nodes can be
  // published into a version.
  const authoredAssetsRef = useRef<Map<string, number> | null>(null);

  // Resolve which version to show: explicit param, else the doc's current.
  const resolvedVersionId = versionParam ?? meta?.currentVersion ?? null;
  const resolvedVersion = useMemo(
    () => versions.find((v) => v.versionId === resolvedVersionId) ?? null,
    [versions, resolvedVersionId]
  );
  const entry = resolvedVersion?.entry ?? "index.html";

  const isEditor =
    !!meta &&
    (meta.ownerEmail === emailLower ||
      (meta.editorMembers ?? meta.editors).includes(emailLower));
  const isOwner = !!meta && meta.ownerEmail === emailLower;
  const isCurrent = !!meta && resolvedVersionId === meta.currentVersion;
  const unresolvedCount = annotations.filter((a) => !a.resolved).length;

  // Two independent routes out of the sandbox (§6c): the owner's global flag,
  // or this account's own opt-in. Either one is enough for THIS reader.
  const effectiveTrusted = !!meta && (meta.trusted !== false || personallyTrusted);
  // Surface trust only where it changes anything: the doc is still sandboxed
  // AND it ships scripts that are consequently not running.
  const trustRelevant = !!meta && !effectiveTrusted && hasScripts;

  // Trust governs READING. Editing is unconditionally script-free (§6a): the
  // bundle's own JS must not run in the frame we serialize, or the DOM it
  // generates gets baked into the saved bytes while the generator stays in
  // place — so every later render appends another copy — and a script that
  // rewrites nodes on load silently discards the editor's typed changes.
  const frameSandboxed = !effectiveTrusted || editing;

  // Show the shared draft (not the published bytes) whenever editing, or when an
  // editor is on the current version and a draft exists. Drafts are editor-only
  // (getDraft is canEdit-gated); viewers always see the last published version.
  const viewingDraft = editing || (isEditor && isCurrent && draftPresent);

  // Keep draftPresent in sync with the loaded doc metadata.
  useEffect(() => {
    setDraftPresent(
      !!meta?.draft && meta.draft.baseVersion === meta.currentVersion
    );
  }, [meta]);

  // --- Load doc meta + versions -----------------------------------------
  useEffect(() => {
    if (!docId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([getDoc(docId), listVersions(docId)])
      .then(([m, vs]) => {
        if (!alive) return;
        if (!m) {
          setError("Document not found, or you do not have access.");
        }
        setMeta(m);
        setVersions(vs);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError("Could not load this document.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [docId, reloadKey]);

  // --- Presence: announce ourselves + watch who else is here ------------
  useEffect(() => {
    if (!docId || !user) return;
    const handle = joinPresence(
      docId,
      user.uid,
      emailLower,
      user.displayName ?? emailLower,
      "view"
    );
    presenceRef.current = handle;
    const unsub = watchPresence(docId, setPresent);
    return () => {
      handle.leave();
      unsub();
      presenceRef.current = null;
    };
  }, [docId, user, emailLower]);

  // Reflect our view/edit mode in presence.
  useEffect(() => {
    presenceRef.current?.setMode(editing ? "edit" : "view");
  }, [editing]);

  // --- Live sync: react to the shared doc changing under us -------------
  // One stable subscription (does not resubscribe on edit/version changes;
  // reads current values via refs). Keeps meta fresh, surfaces a peer's draft
  // save (refresh when idle, soft conflict while editing), and follows new
  // versions published by others.
  useEffect(() => {
    if (!docId) return;
    firstSnapRef.current = true;
    const unsub = watchDoc(docId, (m) => {
      if (!m) return;
      setMeta(m);

      const stamp = m.draft?.updatedAt?.toMillis?.() ?? null;
      const byMe = m.draft?.updatedByEmail === emailLower;
      const prevStamp = lastDraftStampRef.current;
      lastDraftStampRef.current = stamp;

      const prevVersion = lastVersionRef.current;
      lastVersionRef.current = m.currentVersion;

      // First snapshot establishes the baseline; never treat it as a change.
      if (firstSnapRef.current) {
        firstSnapRef.current = false;
        return;
      }

      // A peer published a new version.
      if (m.currentVersion !== prevVersion) {
        if (editingRef.current) {
          if (m.currentVersion !== editBaseRef.current) setStale(true);
        } else {
          setReloadKey((k) => k + 1); // reload versions+meta so the new vN shows
        }
        return;
      }

      // A peer saved the shared draft.
      if (stamp != null && stamp !== prevStamp && !byMe) {
        const who = m.draft?.updatedByEmail ?? "Someone";
        if (editingRef.current) {
          setRemoteEditor(who); // soft conflict — let the user choose
        } else {
          // Refresh the draft view, preserving scroll position.
          const idoc = iframeRef.current?.contentDocument;
          restoreScrollRef.current =
            idoc?.documentElement?.scrollTop ?? idoc?.body?.scrollTop ?? 0;
          setDraftReloadToken((t) => t + 1);
        }
      }
    });
    return unsub;
  }, [docId, emailLower]);

  // --- Subscribe to annotations for the active version ------------------
  useEffect(() => {
    if (!docId || !resolvedVersionId) return;
    setAnnotations([]);
    setSelectedAnnId(null);
    setPendingAnchor(null);
    // Hand the comments panel back to the automatic rule: a manual open/close
    // was about the doc the reader just left, not the one they are opening.
    // Same for a dismissed trust banner — DocViewer is NOT remounted when the
    // route params change, so per-doc UI state has to be reset explicitly.
    setCommentsPref(null);
    // `pane` was the one piece of viewer UI state that leaked: open comments on a
    // phone, tap through to another doc, and you landed on its comments pane.
    setPane("doc");
    setTrustBannerDismissed(false);
    const unsub = listAnnotations(docId, resolvedVersionId, setAnnotations);
    return unsub;
  }, [docId, resolvedVersionId]);

  // --- Personal trust (§6c): does THIS account trust this doc? -----------
  // Keyed on uid, not the User object: onIdTokenChanged hands us a fresh User on
  // every hourly token refresh, and resubscribing on that churn is pointless.
  const uid = user?.uid;
  useEffect(() => {
    if (!docId || !uid) return;
    setPersonallyTrusted(false);
    return watchPersonalTrust(docId, uid, setPersonallyTrusted);
  }, [docId, uid]);

  // --- Record the reading session (CONTRACTS §2 POST /view) --------------
  // One session per doc+version view. The tracker owns the clock; the iframe
  // interaction listeners below feed it activity so idle time is not counted.
  // Depends on uid for the same reason — restarting on a token refresh would
  // close the session and open a new one, logging a phantom second view every
  // hour of continuous reading.
  const trackerRef = useRef<ViewTracker | null>(null);
  useEffect(() => {
    if (!docId || !resolvedVersionId || !uid) return;
    const tracker = trackView(docId, resolvedVersionId);
    trackerRef.current = tracker;
    return () => {
      tracker.stop();
      trackerRef.current = null;
    };
  }, [docId, resolvedVersionId, uid]);

  // Feed the tracker real interaction. Listens on BOTH the parent (toolbar,
  // sidebar, comments) and the same-origin iframe document (where the actual
  // reading happens) — an iframe scroll never reaches the parent window, so
  // without the inner listeners a reader scrolling a long doc would look idle.
  // Runs regardless of edit mode; typing is activity too.
  useEffect(() => {
    const mark = () => trackerRef.current?.markActive();
    const idoc = iframeRef.current?.contentDocument;
    const targets: (Document | Window)[] = [window];
    if (idoc) targets.push(idoc);
    const events = ["pointerdown", "keydown", "scroll", "wheel"] as const;
    for (const t of targets) {
      for (const e of events) {
        t.addEventListener(e, mark, { passive: true });
      }
    }
    return () => {
      for (const t of targets) {
        for (const e of events) t.removeEventListener(e, mark);
      }
    };
  }, [docEpoch]);

  // --- Wire annotation interactions on each load (view mode only) -------
  useEffect(() => {
    if (editing) return;
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc) return;

    ensureHighlightStyles(idoc);

    // Tapping an existing highlight is deliberate intent, so it still opens the
    // panel — including when the same highlight is clicked twice, which the old
    // state-derived effect could not do.
    const offClick = attachHighlightClick(idoc, (annId) => {
      setSelectedAnnId(annId);
      setPendingAnchor(null);
      setCommentsVisible(true);
    });

    // Position the floating "Add comment" button next to the live selection.
    // Selection rects are relative to the IFRAME viewport; add the iframe's
    // offset in the parent to place a position:fixed button over it.
    const positionFab = () => {
      // Touch has no hover affordance, so it always gets the FAB. A mouse gets it
      // only while the panel is collapsed: that is the replacement for the old
      // auto-open, and it is a real <button>, so the keyboard route from a
      // selection to the composer survives.
      if (!coarse && commentsVisibleRef.current) {
        setSelAction(null);
        return;
      }
      const sel = idoc.getSelection();
      const range =
        sel && sel.rangeCount && !sel.isCollapsed ? sel.getRangeAt(0) : null;
      const rect = range?.getBoundingClientRect();
      const frame = iframeRef.current?.getBoundingClientRect();
      if (!rect || !frame || rect.width + rect.height === 0) {
        setSelAction(null);
        return;
      }
      // Hide when the selection has scrolled out of the iframe's viewport.
      if (rect.bottom < 0 || rect.top > frame.height) {
        setSelAction(null);
        return;
      }
      const fabH = 40;
      const fabW = 160;
      const margin = 8;
      const above = rect.top - fabH - margin >= 0;
      const top = frame.top + (above ? rect.top - fabH - margin : rect.bottom + margin);
      const left = Math.max(
        margin,
        Math.min(frame.left + rect.left, window.innerWidth - fabW - margin)
      );
      setSelAction({ top, left });
    };

    const captureSelection = () => {
      const anchor = describeAnchorFromSelection(idoc);
      if (anchor) {
        setPendingAnchor(anchor);
        setSelectedAnnId(null);
      }
      positionFab();
    };

    // selectionchange fires continuously during a touch drag; debounce so we
    // only act once the selection settles. pointerup/keyup give an immediate
    // result for mouse + keyboard.
    let selTimer: number | undefined;
    const onSelectionChange = () => {
      window.clearTimeout(selTimer);
      selTimer = window.setTimeout(captureSelection, 250);
    };
    idoc.addEventListener("pointerup", captureSelection);
    idoc.addEventListener("keyup", captureSelection);
    idoc.addEventListener("selectionchange", onSelectionChange);

    // Keep the FAB anchored while the doc scrolls / the viewport resizes.
    const win = idoc.defaultView;
    let raf = 0;
    const reposition = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        positionFab();
      });
    };
    win?.addEventListener("scroll", reposition, { passive: true });
    window.addEventListener("resize", reposition);
    win?.visualViewport?.addEventListener("resize", reposition);
    win?.visualViewport?.addEventListener("scroll", reposition);
    // Showing or hiding the panel is not a selection event, but it changes
    // whether a mouse user should see the FAB — let the visibility effect below
    // nudge the positioner.
    repositionFabRef.current = positionFab;

    return () => {
      repositionFabRef.current = null;
      offClick();
      window.clearTimeout(selTimer);
      if (raf) cancelAnimationFrame(raf);
      idoc.removeEventListener("pointerup", captureSelection);
      idoc.removeEventListener("keyup", captureSelection);
      idoc.removeEventListener("selectionchange", onSelectionChange);
      win?.removeEventListener("scroll", reposition);
      window.removeEventListener("resize", reposition);
      win?.visualViewport?.removeEventListener("resize", reposition);
      win?.visualViewport?.removeEventListener("scroll", reposition);
      setSelAction(null);
    };
  }, [docEpoch, editing, coarse, setCommentsVisible]);

  // --- (Re)draw highlights whenever annotations or the doc change -------
  useEffect(() => {
    if (editing) return;
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc) return;
    ensureHighlightStyles(idoc);
    redrawHighlights(idoc, annotations, (anchor) =>
      rangeFromAnchor(idoc, anchor)
    );
  }, [docEpoch, annotations, editing]);

  // --- Scroll the selected highlight into view --------------------------
  useEffect(() => {
    if (editing) return;
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc || !selectedAnnId) return;
    const mark = idoc.querySelector(
      `mark.soapbox-hl[data-ann-id="${CSS.escape(selectedAnnId)}"]`
    );
    mark?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "center",
    });
  }, [selectedAnnId, docEpoch, annotations, editing]);

  // --- Edit mode: make the doc editable and autosave the draft ----------
  useEffect(() => {
    if (!editing || !docId) return;
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc) return;
    // Toggling edit mode remounts the frame (the sandbox has to be re-parsed),
    // and the fresh frame hands this effect an about:blank before the real
    // navigation commits. Registering flush against it would save an empty
    // draft; the onLoad -> docEpoch bump re-runs us against the real document.
    if (!idoc.body || idoc.URL === "about:blank") return;

    // The draft can arrive without decoration, but strip defensively so the
    // user never edits (and we never serialize) injected marks/styles.
    clearHighlights(idoc);
    removeHighlightStyles(idoc);
    idoc.body.contentEditable = "true";
    // Scripts are off in here, so containers the author left for their script to
    // fill are empty. Outline them rather than showing unexplained blank space.
    markGeneratedContainers(idoc);
    // Format with inline CSS (so font/color/size land as styles, not <font>), and
    // make the brand fonts available inside the deck so font choices render.
    try {
      idoc.execCommand("styleWithCSS", false, "true");
    } catch {
      /* not all engines expose this; formatting still works */
    }
    if (!idoc.getElementById("soapbox-edit-fonts")) {
      const link = idoc.createElement("link");
      link.id = "soapbox-edit-fonts";
      link.setAttribute("data-soapbox", "edit-fonts");
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&family=Rubik:wght@300;400&family=Montserrat:wght@500&display=swap";
      (idoc.head ?? idoc.documentElement).appendChild(link);
    }

    let timer: number | undefined;
    /** Persist the draft. Returns false if it did NOT reach the server. */
    const flush = async (): Promise<boolean> => {
      window.clearTimeout(timer);
      const html = serializeCleanHtml(idoc, {
        authoredAssets: authoredAssetsRef.current ?? undefined,
      });
      setSaveState("saving");
      try {
        await saveDraft(docId, editBaseRef.current, html, entry);
        dirtyRef.current = false;
        setDraftPresent(true);
        setSaveState("saved");
        return true;
      } catch (e) {
        setSaveState("error");
        setEditError((e as Error).message);
        if (e instanceof StaleVersionError) setStale(true);
        return false;
      }
    };
    pendingFlushRef.current = flush;

    const onInput = () => {
      window.clearTimeout(timer);
      dirtyRef.current = true;
      setSaveState("dirty");
      timer = window.setTimeout(flush, 1000);
    };
    idoc.addEventListener("input", onInput);

    return () => {
      window.clearTimeout(timer);
      idoc.removeEventListener("input", onInput);
      // Exiting edit mode: the iframe may NOT reload (draft view keeps the same
      // src), so explicitly drop editability here.
      if (idoc.body) idoc.body.contentEditable = "false";
      // Drop everything we injected. The serializer strips it too, but leaving
      // it in the live DOM means a later non-edit serialize (export) would see
      // chrome that was never part of the doc.
      clearGeneratedContainers(idoc);
      idoc.getElementById("soapbox-edit-fonts")?.remove();
      pendingFlushRef.current = null;
    };
  }, [editing, docEpoch, docId, entry]);

  const onCreatePending = useCallback(
    async (body: string) => {
      if (!pendingAnchor || !user || !docId || !resolvedVersionId) return;
      const annId = await createAnnotation(
        docId,
        resolvedVersionId,
        pendingAnchor,
        user.uid,
        emailLower
      );
      await addComment(
        docId,
        resolvedVersionId,
        annId,
        body,
        user.uid,
        emailLower
      );
      setPendingAnchor(null);
      setSelectedAnnId(annId);
      // Clear the native selection so the highlight reads cleanly.
      iframeRef.current?.contentDocument?.getSelection?.()?.removeAllRanges();
    },
    [pendingAnchor, user, docId, resolvedVersionId, emailLower]
  );

  // --- Edit-mode actions ------------------------------------------------
  const enterEdit = useCallback(() => {
    if (!meta) return;
    editBaseRef.current = meta.currentVersion;
    dirtyRef.current = false;
    setEditError(null);
    setStale(false);
    setRemoteEditor(null);
    setSaveState("idle");
    setSelectedAnnId(null);
    setPendingAnchor(null);
    setShareOpen(false);
    // Entering edit remounts the frame (script-free re-parse), so hold the
    // scroll position for onLoad to restore.
    const idoc = iframeRef.current?.contentDocument;
    restoreScrollRef.current =
      idoc?.documentElement?.scrollTop ?? idoc?.body?.scrollTop ?? 0;
    setEditing(true);
  }, [meta]);

  const exitEdit = useCallback(async () => {
    // Only persist if there are unsaved edits — exiting with no changes must not
    // create a redundant draft.
    if (dirtyRef.current && pendingFlushRef.current) {
      // Deliberately NOT gated on success: this is the only labelled way out of
      // edit mode, so a failed save must surface (flush sets editError) without
      // trapping the user in the editor.
      await pendingFlushRef.current();
    }
    // Leaving edit remounts the frame so the scripts finally run; keep the
    // reader's place across that reload.
    const idoc = iframeRef.current?.contentDocument;
    restoreScrollRef.current =
      idoc?.documentElement?.scrollTop ?? idoc?.body?.scrollTop ?? 0;
    setEditing(false);
    setSaveState("idle");
    setEditError(null);
    setRemoteEditor(null);
  }, []);

  const exec = useCallback((command: string, value?: string) => {
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc) return;
    idoc.execCommand(command, false, value);
  }, []);

  const onCommit = useCallback(async () => {
    if (!docId) return;
    // Commit can be triggered from the view-mode draft banner too, so make sure
    // the base version is set (== current version the draft forks from).
    if (meta?.currentVersion) editBaseRef.current = meta.currentVersion;
    setCommitBusy(true);
    setEditError(null);
    try {
      // Make sure the server draft reflects the latest edits before promoting.
      // If that save failed, STOP: committing anyway would publish the previous
      // draft bytes as a new version while reporting success — the edits the
      // user is looking at would silently not be in it.
      if (pendingFlushRef.current) {
        const saved = await pendingFlushRef.current();
        if (!saved) {
          setEditError(
            "Could not save your latest edits, so nothing was published. " +
              "Check your connection and try again."
          );
          return;
        }
      }
      const result = await commitVersion(docId, editBaseRef.current);
      setEditing(false);
      setSaveState("idle");
      setDraftPresent(false); // draft promoted + cleared server-side
      setReloadKey((k) => k + 1); // reload meta + versions (now includes the new vN)
      navigate(`/d/${docId}/${result.versionId}`);
    } catch (e) {
      if (e instanceof UnresolvedCommentsError) {
        setEditError(e.message);
      } else if (e instanceof StaleVersionError) {
        setStale(true);
        setEditError(e.message);
      } else {
        setEditError((e as Error).message);
      }
    } finally {
      setCommitBusy(false);
    }
  }, [docId, navigate, meta]);

  // --- Presentation mode (full-window, no chrome) -----------------------
  const enterPresent = useCallback(() => {
    setPresenting(true);
    document.body.classList.add("is-presenting");
    viewerRef.current?.requestFullscreen?.().catch(() => {
      /* fullscreen may be blocked; the CSS full-window state still applies */
    });
  }, []);

  const exitPresent = useCallback(() => {
    setPresenting(false);
    document.body.classList.remove("is-presenting");
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
  }, []);

  useEffect(() => {
    if (!presenting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitPresent();
    };
    const onFsChange = () => {
      if (!document.fullscreenElement) {
        setPresenting(false);
        document.body.classList.remove("is-presenting");
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.body.classList.remove("is-presenting");
    };
  }, [presenting, exitPresent]);

  // --- Export actions ---------------------------------------------------
  const contentBaseHref = useCallback(
    () =>
      viewingDraft
        ? `/draft/${docId}/`
        : `/content/${docId}/${resolvedVersionId}/`,
    [viewingDraft, docId, resolvedVersionId]
  );

  const onExportPdf = useCallback(async () => {
    const idoc = iframeRef.current?.contentDocument;
    if (!idoc) {
      toastError("The document is still loading.");
      return;
    }
    try {
      await printDoc(idoc, { baseHref: contentBaseHref() });
    } catch (e) {
      toastError((e as Error).message);
    }
  }, [contentBaseHref, toastError]);

  const onDownloadHtml = useCallback(async () => {
    if (!docId || !resolvedVersionId || !meta) return;
    try {
      await downloadHtml(docId, resolvedVersionId, entry, meta.title);
      success("Download started.");
    } catch (e) {
      toastError((e as Error).message);
    }
  }, [docId, resolvedVersionId, entry, meta, success, toastError]);

  const onDownloadZip = useCallback(() => {
    if (!docId || !resolvedVersionId) return;
    downloadZip(docId, resolvedVersionId);
    success("Download started.");
  }, [docId, resolvedVersionId, success]);

  const onDuplicateThis = useCallback(async () => {
    if (!docId) return;
    try {
      const r = await duplicateDoc(docId, meta?.folderId ?? null);
      success(`Duplicated as “${r.title}”.`);
      navigate(`/d/${r.docId}`);
    } catch (e) {
      toastError((e as Error).message);
    }
  }, [docId, meta, navigate, success, toastError]);

  // Owner lifts the sandbox for EVERY reader (§6c). Optimistically flip meta
  // (watchDoc confirms) and re-key the iframe so it remounts un-sandboxed and the
  // bundle's scripts finally run.
  const onTrustForEveryone = useCallback(async () => {
    if (!docId) return;
    setTrustBusy(true);
    try {
      await setTrusted(docId, true);
      setMeta((m) => (m ? { ...m, trusted: true } : m));
      setTrustReloadToken((t) => t + 1);
      setTrustConfirmOpen(false);
      success("Document trusted for everyone — its scripts now run.");
    } catch (e) {
      toastError((e as Error).message);
    } finally {
      setTrustBusy(false);
    }
  }, [docId, success, toastError]);

  // Any member lifts the sandbox for THEMSELVES only. The write is own-uid
  // gated in firestore.rules and never touches meta.trusted, so no other
  // reader's rendering changes. Same re-key so the scripts actually start.
  const onTrustForMe = useCallback(async () => {
    if (!docId || !user) return;
    setTrustBusy(true);
    try {
      await setPersonalTrust(docId, user.uid, emailLower, true);
      setPersonallyTrusted(true);
      setTrustReloadToken((t) => t + 1);
      setTrustConfirmOpen(false);
      success("Trusted for your account — its scripts now run for you.");
    } catch {
      toastError("Could not trust this document for your account.");
    } finally {
      setTrustBusy(false);
    }
  }, [docId, user, emailLower, success, toastError]);

  const onRevokeMyTrust = useCallback(async () => {
    if (!docId || !user) return;
    try {
      await setPersonalTrust(docId, user.uid, emailLower, false);
      setPersonallyTrusted(false);
      setTrustReloadToken((t) => t + 1);
      success("Your trust was removed — this document is sandboxed again.");
    } catch {
      toastError("Could not remove your trust.");
    }
  }, [docId, user, emailLower, success, toastError]);

  // --- Probe the loaded document ----------------------------------------
  // Detect a section.section deck, and whether the bundle ships scripts. The
  // sandbox blocks script EXECUTION but leaves the tags in the parsed DOM, so
  // this is a reliable read of "would trusting this change anything?".
  useEffect(() => {
    const idoc = iframeRef.current?.contentDocument;
    setIsSlideDeck(!!idoc?.querySelector("section.section"));
    setHasScripts(!!idoc?.querySelector("script"));
  }, [docEpoch]);

  // Slide add/reorder/delete are DOM edits that don't fire `input`, so flush the
  // draft explicitly (only reachable while editing, when pendingFlushRef is set).
  const onSlideChange = useCallback(() => {
    dirtyRef.current = true;
    setSaveState("dirty");
    void pendingFlushRef.current?.();
  }, []);

  if (!docId) return null;

  if (loading) {
    return (
      <div className="container">
        <p className="empty-state">Loading document…</p>
      </div>
    );
  }

  if (error || !meta || !resolvedVersionId) {
    return (
      <div className="container">
        <div className="error-banner" role="alert">
          {error ?? "Document unavailable."}
        </div>
        <button type="button" className="btn" onClick={() => navigate("/")}>
          Back to documents
        </button>
      </div>
    );
  }

  // The ?r token forces the draft iframe to refetch when a peer's save lands
  // (getDraft ignores the query; Cache-Control is no-store). Only applied in
  // the draft view, and never bumped while we are editing.
  const src = viewingDraft
    ? `/draft/${docId}/${entry}?r=${draftReloadToken}`
    : `/content/${docId}/${resolvedVersionId}/${entry}`;

  const saveLabel: Record<SaveState, string> = {
    idle: "",
    dirty: "Unsaved changes…",
    saving: "Saving…",
    saved: "Draft saved",
    error: "Save failed",
  };

  const multiFile = (resolvedVersion?.fileCount ?? 1) > 1;

  const sharePanel = (
    <SharePanel
      ownerEmail={meta.ownerEmail}
      viewers={meta.viewers}
      editors={meta.editors}
      onApply={(c) => setAccess(docId, c)}
      onChange={(next) =>
        setMeta((m) =>
          m
            ? {
                ...m,
                viewers: next.viewers,
                editors: next.editors,
                members: next.members ?? m.members,
              }
            : m
        )
      }
      onClose={() => setShareOpen(false)}
    />
  );

  return (
    <div
      className={`viewer${presenting ? " is-presenting" : ""}${
        isSlideDeck ? " viewer--slides" : ""
      }${
        isSingleColumn ? (pane === "comments" ? " show-comments" : " show-doc") : ""
      }${
        // Single-column mode has its own Doc/Comments tabs, so the collapse only
        // applies to the two-column desktop grid.
        !isSingleColumn && !commentsVisible ? " comments-collapsed" : ""
      }`}
      ref={viewerRef}
    >
      {isSlideDeck && !presenting && (
        <SlidePanel
          getDoc={() => iframeRef.current?.contentDocument ?? null}
          editing={editing}
          docEpoch={docEpoch}
          onContentChanged={onSlideChange}
        />
      )}
      <div className="viewer-main">
        <ViewerToolbar
          meta={meta}
          versions={versions}
          resolvedVersionId={resolvedVersionId}
          multiFile={multiFile}
          isOwner={isOwner}
          isEditor={isEditor}
          isCurrent={isCurrent}
          effectiveTrusted={effectiveTrusted}
          personallyTrusted={personallyTrusted}
          editing={editing}
          saveLabel={saveLabel[saveState]}
          commitBusy={commitBusy}
          saving={saveState === "saving"}
          unresolvedCount={unresolvedCount}
          commitBlocked={unresolvedCount > 0 || stale}
          showCommentsToggle={!isSingleColumn}
          commentsOpen={commentsVisible}
          threadCount={annotations.length}
          present={present}
          meUid={user?.uid}
          shareOpen={shareOpen}
          sharePanel={sharePanel}
          onBack={() => navigate("/")}
          onSelectVersion={(v) => navigate(`/d/${docId}/${v}`)}
          onToggleComments={toggleComments}
          onPresent={enterPresent}
          onToggleShare={() => setShareOpen((o) => !o)}
          onOpenTrust={() => setTrustConfirmOpen(true)}
          onRevokeMyTrust={onRevokeMyTrust}
          onEnterEdit={enterEdit}
          onExitEdit={exitEdit}
          onCommit={onCommit}
          onExportPdf={onExportPdf}
          onExportImage={() => setImageExportOpen(true)}
          onDownloadHtml={onDownloadHtml}
          onDownloadZip={onDownloadZip}
          onDuplicate={onDuplicateThis}
          onViewStats={() => setStatsOpen(true)}
        />

        {/* Sandboxed doc whose scripts are consequently NOT running — the one
            case where the reader needs to know why the page looks wrong. Static
            uploads never reach here, so this is not a nag. */}
        {trustRelevant && !editing && !presenting && !trustBannerDismissed && (
          <div className="trust-bar" role="status">
            <Icon name="shield" size={18} />
            <span className="caption">
              This document’s own scripts are blocked, so it may look blank or
              unstyled.
            </span>
            <span style={{ marginLeft: "auto" }} />
            <button
              type="button"
              className="btn btn-warn"
              onClick={() => setTrustConfirmOpen(true)}
            >
              Trust…
            </button>
            <button
              type="button"
              className="btn btn-ghost icon-btn"
              onClick={() => setTrustBannerDismissed(true)}
              aria-label="Dismiss"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        )}

        {editing && (
          <div className="edit-bar">
            <EditToolbar
              exec={exec}
              getDoc={() => iframeRef.current?.contentDocument ?? null}
            />
          </div>
        )}

        {editing && hasScripts && (
          <div className="edit-note caption" role="status">
            You are editing this doc's source. Its scripts are off while you
            edit, so the outlined blocks are empty here — they are rebuilt when
            the doc is read, and cannot be changed from the editor. Everything
            else is yours to edit. "Done editing" shows it as readers see it.
          </div>
        )}

        {editing && unresolvedCount > 0 && (
          <div className="edit-note caption" role="status">
            {unresolvedCount} unresolved comment{unresolvedCount === 1 ? "" : "s"}
            {" "}— resolve all to publish a new version.
          </div>
        )}

        {!editing && viewingDraft && (
          <div className="edit-bar" role="status">
            <span className="caption">
              Showing an unpublished draft — your edits are saved but not yet
              published.
            </span>
            <span style={{ marginLeft: "auto" }} />
            <button type="button" className="btn btn-ghost" onClick={enterEdit}>
              Continue editing
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onCommit}
              disabled={commitBusy || unresolvedCount > 0}
              title={
                unresolvedCount > 0
                  ? "Resolve all comments before publishing a new version"
                  : "Publish a new immutable version from this draft"
              }
            >
              {commitBusy ? "Publishing…" : "Save as new version"}
            </button>
          </div>
        )}

        {!editing && viewingDraft && unresolvedCount > 0 && (
          <div className="edit-note caption" role="status">
            {unresolvedCount} unresolved comment{unresolvedCount === 1 ? "" : "s"}
            {" "}— resolve all to publish this draft as a new version.
          </div>
        )}

        {editing && stale && (
          <div className="error-banner" role="alert">
            A newer version was published by someone else. Reload to continue.
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setEditing(false);
                setStale(false);
                setReloadKey((k) => k + 1);
                navigate(`/d/${docId}`);
              }}
            >
              Reload
            </button>
          </div>
        )}

        {editing && editError && !stale && (
          <div className="error-banner" role="alert">
            {editError}
          </div>
        )}

        {editing && remoteEditor && !stale && (
          <div className="edit-note caption" role="alert">
            {remoteEditor} updated this draft while you were editing.
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                // Discard local unsaved edits and load their version.
                dirtyRef.current = false;
                setRemoteEditor(null);
                setEditing(false);
                setDraftReloadToken((t) => t + 1);
              }}
            >
              Reload theirs
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setRemoteEditor(null)}
            >
              Keep mine
            </button>
          </div>
        )}

        {presenting && (
          <button
            type="button"
            className="present-exit btn"
            onClick={exitPresent}
            aria-label="Exit presentation"
          >
            <Icon name="close" size={18} />
            Exit
          </button>
        )}

        {isSingleColumn && !presenting && (
          <div className="viewer-tabs" role="group" aria-label="View document or comments">
            <button
              type="button"
              className="viewer-tab"
              aria-pressed={pane === "doc"}
              onClick={() => setPane("doc")}
            >
              Document
            </button>
            {/* Same id as the desktop toolbar toggle: the two are mutually
                exclusive (this renders only when isSingleColumn, that one only
                when it is not), so closing the panel can hand focus back with
                one lookup at any width. */}
            <button
              type="button"
              id={COMMENTS_TOGGLE_ID}
              className="viewer-tab"
              aria-pressed={pane === "comments"}
              aria-controls="soapbox-comments"
              onClick={() => setPane("comments")}
            >
              Comments{annotations.length ? ` · ${annotations.length}` : ""}
            </button>
          </div>
        )}

        <iframe
          // Re-key on trust so the frame remounts and re-parses without the
          // sandbox once the doc is trusted — globally by the owner or just by
          // this account (a changed sandbox attr alone won't re-run the
          // previously-blocked scripts).
          // Sandboxing is decided at PARSE time, so changing the attribute alone
          // does nothing to an already-loaded document — the frame has to
          // remount. Entering edit from a draft view keeps an identical src, so
          // without frameSandboxed in the key that path would still attach
          // contentEditable to a script-built DOM.
          key={`${resolvedVersionId}-${frameSandboxed ? "sandboxed" : "scripts"}-${trustReloadToken}`}
          ref={iframeRef}
          className="viewer-frame"
          src={src}
          // Untrusted bundles render sandboxed: same-origin (so the parent still
          // drives annotation/editing on contentDocument) but WITHOUT
          // allow-scripts, so the bundle's JS can't read the viewer's auth
          // token. Lifted once THIS reader trusts the doc, by either route —
          // except while editing, which is always script-free (see above).
          sandbox={frameSandboxed ? "allow-same-origin" : undefined}
          title={`${meta.title} — ${viewingDraft ? "draft" : resolvedVersionId}`}
          onLoad={() => {
            // Capture the author's asset inventory FIRST: onLoad runs before the
            // effects that the docEpoch bump schedules, so nothing of ours (nor
            // anything an extension adds later) is in the baseline.
            const loaded = iframeRef.current?.contentDocument;
            authoredAssetsRef.current = loaded ? captureAssetTags(loaded) : null;
            setDocEpoch((n) => n + 1);
            // Restore scroll after a live refresh triggered by a peer's save.
            const s = restoreScrollRef.current;
            if (s != null) {
              const idoc = iframeRef.current?.contentDocument;
              if (idoc) {
                if (idoc.documentElement) idoc.documentElement.scrollTop = s;
                if (idoc.body) idoc.body.scrollTop = s;
              }
              restoreScrollRef.current = null;
            }
          }}
        />
      </div>

      <Sidebar
        docId={docId}
        versionId={resolvedVersionId}
        annotations={annotations}
        selectedAnnId={selectedAnnId}
        onSelect={setSelectedAnnId}
        pending={pendingAnchor}
        onCreatePending={onCreatePending}
        onCancelPending={() => setPendingAnchor(null)}
        onClose={closeComments}
      />

      {/* Floating action to open the composer for the current selection. Lives in
          the parent (over the iframe) so tapping never collapses the in-iframe
          selection. Shown for touch, which has no hover affordance, and for a
          mouse whenever the panel is collapsed — that is what replaced the old
          "any selection force-opens the panel", which is why it never stayed
          closed. Never while presenting: the chrome is meant to be gone. */}
      {(coarse || !commentsVisible) && selAction && !editing && !presenting && (
        <button
          type="button"
          className="selection-fab"
          style={{ top: selAction.top, left: selAction.left }}
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => {
            setCommentsVisible(true);
            setSelAction(null);
          }}
        >
          <Icon name="plus" size={16} />
          Comment
        </button>
      )}

      {imageExportOpen && (
        <ImageExportDialog
          getDoc={() => iframeRef.current?.contentDocument ?? null}
          title={meta.slug || meta.title}
          onClose={() => setImageExportOpen(false)}
        />
      )}

      {trustConfirmOpen && (
        <Modal
          title="Trust this document?"
          onClose={() => setTrustConfirmOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setTrustConfirmOpen(false)}
                disabled={trustBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={isOwner ? "btn btn-ghost" : "btn btn-primary"}
                onClick={onTrustForMe}
                disabled={trustBusy}
              >
                {trustBusy ? "Trusting…" : "Trust just for me"}
              </button>
              {isOwner && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onTrustForEveryone}
                  disabled={trustBusy}
                >
                  {trustBusy ? "Trusting…" : "Trust for everyone"}
                </button>
              )}
            </>
          }
        >
          <p>
            Uploaded documents are sandboxed: their embedded scripts don’t run, so a
            deck that reveals its slides with JavaScript can look blank.
          </p>
          <p>
            <strong>Trust just for me</strong> lets{" "}
            <strong>{meta.title}</strong>’s scripts run in your browser only. The
            single session exposed to them is your own, and nobody else’s view
            changes. You can undo it any time from the More menu.
          </p>
          {isOwner ? (
            <p>
              <strong>Trust for everyone</strong> lifts the sandbox for{" "}
              <strong>every person who can view this document</strong>, running its
              scripts with the same access as each reader. Only do that for
              documents you produced or have fully vetted.
            </p>
          ) : (
            <p className="caption">
              Only the owner ({meta.ownerEmail}) can trust this document for
              everyone who can view it.
            </p>
          )}
        </Modal>
      )}

      {statsOpen && (
        <StatsPanel
          meta={meta}
          versionCount={versions.length}
          annotations={annotations}
          canSeeDetail={isEditor}
          onClose={() => setStatsOpen(false)}
        />
      )}

      {/* On phone, Share is a "More" menu item; render its panel as a centered
          modal (there's no toolbar anchor button to hang a popover from). */}
      {isOwner && isPhone && shareOpen && (
        <div
          className="share-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShareOpen(false);
          }}
        >
          {sharePanel}
        </div>
      )}
    </div>
  );
}
